import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '@/components/shared/PageHeader';
import { MyTicketConversation, useMyTicket } from '@/components/support/MyTicketConversation';
import { CircleNotch } from '@phosphor-icons/react';
import { Card, CardContent } from '@/components/ui/card';
import type { SupportStatus } from '@/lib/supportApi';

const STATUS_META: Record<SupportStatus, { label: string; className: string; note: string }> = {
  open: { label: 'Open', className: 'bg-info-bg text-info', note: 'We have your report and will be in touch.' },
  in_progress: { label: 'In progress', className: 'bg-warning-bg text-warning', note: 'Support is looking into this.' },
  resolved: { label: 'Resolved', className: 'bg-success-bg text-success', note: 'Support marked this resolved. Reply below if it is still happening.' },
  closed: { label: 'Closed', className: 'bg-muted text-muted-foreground', note: 'This ticket is closed. Replying will reopen it.' },
};

function fmt(s: string) {
  return new Date(s).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export default function MySupportTicketPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // The SAME query the row-level conversation drawer uses — one key, one fetch.
  const { data: ticket, isLoading, error } = useMyTicket(id);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <CircleNotch className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // A ticket that is not yours returns 404 — the server never confirms it exists. See
  // support.service.ts. The message here must not imply otherwise.
  if (error || !ticket) {
    return (
      <div>
        <PageHeader title="Support ticket" onBack={() => navigate('/me/support')} backLabel="All tickets" />
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">
          This support ticket could not be found.
        </CardContent></Card>
      </div>
    );
  }

  const meta = STATUS_META[ticket.status] ?? STATUS_META.open;

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        title={ticket.title}
        subtitle={`Raised ${fmt(ticket.createdAt)}`}
        onBack={() => navigate('/me/support')}
        backLabel="All tickets"
        actions={
          <span className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-medium ${meta.className}`}>
            {meta.label}
          </span>
        }
      />

      <p className="mb-5 text-sm text-muted-foreground">{meta.note}</p>

      {/* The conversation itself — the original report, every reply, and the composer. Identical
          to what the row-level 💬 action opens in a drawer, because it IS that component. */}
      <MyTicketConversation ticketId={ticket.id} />
    </div>
  );
}
